import { HttpErrorResponse, HttpEvent, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, switchMap, throwError } from 'rxjs';
import { BuildVersionService } from '../../services/build-version.service';
import { ErrorMessageService } from '../../services/error-message.service';
import { STALE_BUNDLE_MESSAGE, STALE_BUNDLE_STATUS_TEXT, STALE_BUNDLE_TITLE } from '../../services/stale-bundle-messages';

export { STALE_BUNDLE_MESSAGE, STALE_BUNDLE_STATUS_TEXT, STALE_BUNDLE_TITLE };

/** Methods that mutate server state — the only ones this guard ever refuses. */
const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * WAM-602 — once `BuildVersionService` confirms this tab is running a stale
 * bundle, refuse every mutating request client-side rather than let it reach
 * a backend that may have already moved on. Written once, centrally, as an
 * interceptor rather than per-screen.
 *
 * 🔴 This also blocks some REQUESTS THAT ARE READS. This app's legacy `.NET`
 * naming convention (`Get*Remote`) does not reliably match the HTTP method:
 * `/aeAMSgetAnnualUpdateSchema/` and `/public/aeAMSloginUser/` (login itself)
 * are both POSTed via `ApiCallService`. Blocking every POST/PUT/PATCH/DELETE
 * is a deliberate, safe-by-default choice — under-blocking a write is the
 * dangerous failure mode, over-blocking an occasional read is not.
 *
 * The actual gate is `BuildVersionService.confirmBeforeWrite()`, not a bare
 * signal read: a CONFIRMED, enforceable mismatch resolves immediately, but a
 * mismatch seen on only ONE poll so far is a real risk window — up to a full
 * poll interval could pass before the confirming second poll, during which
 * every write would otherwise sail through unguarded. `confirmBeforeWrite()`
 * forces a check and waits (capped, fails open on timeout) exactly for that
 * window; see its own doc for the rest of the fail-open guarantees (an
 * unstamped local build, a check that has never run or only ever failed,
 * a suspected reload lockout, …).
 *
 * (WAM-594 note: this is a client-side-only guard. The cut-over endpoints it
 * exists to protect should additionally carry a server-side `X-Build-Id`
 * backstop — tracked as separate follow-up work, not built here.)
 *
 * The blocked request is never sent to the network — `next(req)` is never
 * called on that path — so nothing the user typed is lost to a
 * half-completed save; the calling component's own form state is exactly
 * what it was before the click. A clear message is shown via the same modal
 * every other API failure in this app already uses (`ErrorMessageService`),
 * but only ONCE per tab (`shouldShowWriteRefusalModal()`) — the persistent
 * banner is the ongoing explanation for every refusal after the first. The
 * synthetic error carries status 409 (so `ErrorInterceptor` does not also
 * show its generic "unknown error" modal on top of this one) and a
 * `statusText` `sentry-filter.ts` specifically excludes from Sentry
 * reporting — an expected, user-visible refusal is not "the app broke",
 * and plenty of call sites `.subscribe()` this with no error handler.
 */
export const staleBundleWriteGuardInterceptor: HttpInterceptorFn = (req, next): Observable<HttpEvent<unknown>> => {
  if (!MUTATING_METHODS.has(req.method)) return next(req);

  // Both grabbed synchronously, in the interceptor's injection context —
  // `inject()` cannot run later inside the switchMap callback below.
  const buildVersion = inject(BuildVersionService);
  const errorMessageService = inject(ErrorMessageService);

  return buildVersion.confirmBeforeWrite().pipe(
    switchMap((blocked) => {
      if (!blocked) return next(req);

      if (buildVersion.shouldShowWriteRefusalModal()) {
        errorMessageService.showErrorModal(STALE_BUNDLE_TITLE, [STALE_BUNDLE_MESSAGE]);
      }

      return throwError(
        () =>
          new HttpErrorResponse({
            status: 409,
            statusText: STALE_BUNDLE_STATUS_TEXT,
            url: req.url,
            error: { message: STALE_BUNDLE_MESSAGE },
          }),
      );
    }),
  );
};
